import React, { useMemo, useState } from 'react'
import { Button } from '../shared/Button'
import { crmToast, SearchInput } from './crmUi'
import { obGetLink, obRotateToken, type Onboarding } from './crmApi'

/**
 * EMBED modal (Onboardings Phase 6): the common iframe template WeQuote pastes
 * ONCE (they swap in each company's values server-side — or use our per-company
 * copies below), plus a searchable list of every company with Copy link /
 * Copy embed / Preview and a token-rotate escape hatch.
 */

const TEMPLATE = `<iframe
  src="https://wetask.cloud/crm/onboarding/{company_name}?token={token_id}"
  title="Onboarding"
  style="width:100%;min-height:900px;border:0;"
  allow="clipboard-write"
  loading="lazy">
</iframe>`

export const ObEmbedModal: React.FC<{ list: Onboarding[]; onClose: () => void }> = ({ list, onClose }) => {
  const [q, setQ] = useState('')
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return list.filter((o) => !needle || o.companyName.toLowerCase().includes(needle) || o.slug.includes(needle))
  }, [list, q])

  const copy = (text: string, what: string) => navigator.clipboard?.writeText(text).then(() => crmToast(`${what} copied`, 'success'))
  const withLink = (o: Onboarding, fn: (url: string) => void) =>
    obGetLink(o.id).then((l) => fn(l.url)).catch(() =>
      obRotateToken(o.id).then(() => obGetLink(o.id).then((l) => { crmToast('Token generated (old links were none)', 'info'); fn(l.url) })).catch(() => crmToast('Could not get the link', 'error')),
    )
  const embedFor = (url: string) => TEMPLATE.replace('https://wetask.cloud/crm/onboarding/{company_name}?token={token_id}', url)
  const colLabel: Record<string, string> = { notstarted: 'Not Started', progress: 'In Progress', complete: 'Complete' }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={onClose}>
      <div className="w-[720px] max-h-[85vh] flex flex-col rounded-xl bg-surface border border-border shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <p className="text-[15px] font-bold text-txt-primary">Onboarding embed</p>
          <button onClick={onClose} className="text-txt-tertiary hover:text-txt-primary">✕</button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary">Common template — WeQuote swaps in each company's values</p>
              <Button size="sm" variant="secondary" onClick={() => copy(TEMPLATE, 'Template')}>Copy code</Button>
            </div>
            <pre className="rounded-lg bg-surface-secondary border border-border p-3 text-[10px] text-txt-secondary overflow-x-auto">{TEMPLATE}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary">Per company ({rows.length})</p>
              <SearchInput value={q} onChange={setQ} placeholder="Search companies…" compact className="w-60" />
            </div>
            <div className="rounded-lg border border-border divide-y divide-border max-h-[360px] overflow-y-auto">
              {rows.map((o) => (
                <div key={o.id} className="flex items-center gap-2 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-txt-primary truncate">{o.companyName}</p>
                    <p className="text-[10px] text-txt-tertiary truncate">/crm/onboarding/{o.slug}</p>
                  </div>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-tertiary text-txt-secondary border border-border shrink-0">
                    {o.planState === 'awaiting' ? 'Awaiting plan' : o.planState === 'requested' ? 'Onboarding required' : colLabel[o.column] || o.column}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => withLink(o, (u) => copy(u, 'Link'))}>Copy link</Button>
                  <Button size="sm" variant="ghost" onClick={() => withLink(o, (u) => copy(embedFor(u), 'Embed'))}>Copy embed</Button>
                  <Button size="sm" variant="secondary" onClick={() => withLink(o, (u) => window.open(u, '_blank'))}>Preview</Button>
                </div>
              ))}
              {rows.length === 0 && <p className="px-3 py-4 text-[12px] text-txt-tertiary text-center">No companies match.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
